import React, { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, TooltipContent, TooltipTrigger, useImageViewer } from '@ohif/ui-next';
import { useDiagnosisStatus } from '../../hooks/useDiagnosisStatus';
import { DiagnosisState } from '../../utils';
import ButtonContent from './ButtonContent';
import AssessmentDialog from './AssessmentDialog';
import './DiagnosisButtonStyles.css';

const BASE_CLASS =
  'diagnosis-button flex h-10 min-w-[160px] items-center justify-center gap-2 px-4 font-medium transition-all duration-300';

interface ImageViewerContextValue {
  StudyInstanceUIDs: string[];
}

export default function DiagnosisButton({ tooltip }: { tooltip?: string }) {
  const imageViewer = useImageViewer() as ImageViewerContextValue | null;
  const studyInstanceUid = imageViewer?.StudyInstanceUIDs?.[0];

  const { state, trigger, revoke } = useDiagnosisStatus(studyInstanceUid);
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (state !== DiagnosisState.REQUIRES_ASSESSMENT || !studyInstanceUid) {
      setAssessmentOpen(false);
    }
  }, [state, studyInstanceUid]);

  const handlers: Record<DiagnosisState, (() => void) | undefined> = {
    [DiagnosisState.IDLE]: trigger,
    [DiagnosisState.PROCESSING]: undefined,
    [DiagnosisState.REQUIRES_ASSESSMENT]: () => setAssessmentOpen(true),
    [DiagnosisState.ASSESSED]: undefined,
    [DiagnosisState.REVOCABLE]: revoke,
  };

  const onClick = handlers[state];

  const isDisabled = !studyInstanceUid || !onClick;

  const className = [
    BASE_CLASS,
    state === DiagnosisState.PROCESSING ? 'in-progress' : '',
    state === DiagnosisState.ASSESSED ? 'assessed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            dataCY="sycai-diagnosis-button"
            className={className}
            aria-disabled={isDisabled || undefined}
            onClick={onClick}
          >
            <ButtonContent state={state} />
          </Button>
        </TooltipTrigger>
        {tooltip && <TooltipContent side="bottom">{tooltip}</TooltipContent>}
      </Tooltip>

      {state === DiagnosisState.REQUIRES_ASSESSMENT && studyInstanceUid && (
        <AssessmentDialog
          open={assessmentOpen}
          onOpenChange={setAssessmentOpen}
          studyInstanceUid={studyInstanceUid}
          returnFocusRef={buttonRef}
        />
      )}
    </>
  );
}
